import { Head, Link } from "@inertiajs/react"
import {
  ArrowLeft,
  CheckCircle2,
  Mail,
  ShieldAlert,
  ShieldCheck,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"
import { nameOrDash } from "@/lib/admin-placeholder"
import { adminBrandPath, adminCreatorsPath } from "@/routes"
import type { CreatorShowProps } from "@/types/admin"

/**
 * Admin/Creators/Show — read-only creator detail (OKS-15 AC4 + creator).
 *
 * No mutations on this page in v1. Blocklist toggling lives on
 * `/admin/blocklist`; this page just surfaces the active blocklist
 * status (or "Not on the blocklist") so admins reviewing a creator
 * profile see the flag in context.
 */
export default function AdminCreatorsShow({
  creator,
  memberships,
  blocklist_entry,
}: CreatorShowProps) {
  const { t, i18n } = useTranslation()

  const dateFmt = new Intl.DateTimeFormat(i18n.language, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })

  return (
    <AppLayout>
      <Head title={creator.name} />
      <div
        className="flex flex-col gap-6 p-6"
        data-testid="admin-creator-detail"
        data-creator-id={creator.id}
      >
        <header className="flex flex-col gap-2">
          <Link
            href={adminCreatorsPath()}
            className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1 text-sm"
          >
            <ArrowLeft className="size-4" aria-hidden="true" />
            {t("admin.creator_detail.back")}
          </Link>
          <div className="flex items-center gap-3">
            <h1 className="text-foreground text-2xl font-semibold tracking-tight">
              {creator.name}
            </h1>
            {blocklist_entry && (
              <Badge
                variant="destructive"
                className="inline-flex items-center gap-1"
                data-testid="admin-creator-blocklist-flag"
              >
                <ShieldAlert className="size-3" aria-hidden="true" />
                {t("admin.creators_list.blocklisted_badge")}
              </Badge>
            )}
          </div>
        </header>

        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>{t("admin.creator_detail.cards.profile")}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 text-sm">
              <Row
                icon={Mail}
                label={t("admin.creator_detail.profile.email")}
                value={creator.email}
              />
              <Row
                label={t("admin.creator_detail.profile.tiktok_handle")}
                value={
                  creator.tiktok_handle
                    ? `@${creator.tiktok_handle}`
                    : t("admin.creators_list.no_handle")
                }
              />
              <Row
                label={t("admin.creator_detail.profile.created_at")}
                value={dateFmt.format(new Date(creator.created_at))}
              />
              <Row
                icon={creator.verified ? CheckCircle2 : undefined}
                label={t("admin.creator_detail.profile.verified")}
                value={
                  // Literal-string `t()` per branch so
                  // `inertia_i18n_health_spec`'s static scanner sees
                  // both keys (ternary-inside-t hides them).
                  creator.verified
                    ? t("admin.creator_detail.profile.verified_yes")
                    : t("admin.creator_detail.profile.verified_no")
                }
              />
            </CardContent>
          </Card>

          <Card data-testid="admin-creator-blocklist-status">
            <CardHeader>
              <CardTitle className="inline-flex items-center gap-2">
                {blocklist_entry ? (
                  <ShieldAlert
                    className="text-destructive size-4"
                    aria-hidden="true"
                  />
                ) : (
                  <ShieldCheck
                    className="text-muted-foreground size-4"
                    aria-hidden="true"
                  />
                )}
                {t("admin.creator_detail.cards.blocklist")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              {blocklist_entry ? (
                <div className="flex flex-col gap-2">
                  <span className="text-muted-foreground">
                    {t("admin.creator_detail.blocklist.flagged_by", {
                      actor: blocklist_entry.blocked_by.name,
                      date: dateFmt.format(
                        new Date(blocklist_entry.blocked_at),
                      ),
                    })}
                  </span>
                  <div className="bg-destructive/10 border-destructive/30 rounded-md border px-3 py-2">
                    <p className="text-muted-foreground mb-0.5 text-xs">
                      {t("admin.creator_detail.blocklist.reason_label")}
                    </p>
                    <p className="text-foreground">{blocklist_entry.reason}</p>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground">
                  {t("admin.creator_detail.blocklist.not_flagged")}
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>
                {t("admin.creator_detail.cards.memberships")}{" "}
                <span className="text-muted-foreground ml-1 text-sm font-normal">
                  ({memberships.length})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {memberships.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  {t("admin.creator_detail.memberships_empty")}
                </p>
              ) : (
                <ul
                  className="flex flex-col gap-2"
                  role="list"
                  data-testid="admin-creator-memberships"
                >
                  {memberships.map((m) => (
                    <li
                      key={`${m.brand.id}-${m.joined_at}`}
                      className="flex items-start gap-3 text-sm"
                    >
                      <div className="flex min-w-0 flex-col">
                        <Link
                          href={adminBrandPath({ id: m.brand.id })}
                          className="text-foreground font-medium hover:underline"
                        >
                          {nameOrDash(m.brand.name)}
                        </Link>
                        <span className="text-muted-foreground text-xs">
                          {dateFmt.format(new Date(m.joined_at))}
                          {m.archived_at && (
                            <>
                              {" → "}
                              {dateFmt.format(new Date(m.archived_at))}
                            </>
                          )}
                        </span>
                      </div>
                      <Badge
                        variant={
                          m.status === "active" ? "secondary" : "outline"
                        }
                        className="ml-auto"
                      >
                        {t(
                          `admin.creator_detail.memberships_status.${m.status}`,
                          { defaultValue: m.status },
                        )}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  )
}

interface RowProps {
  icon?: React.ComponentType<{ className?: string; "aria-hidden"?: "true" }>
  label: string
  value: string
}

function Row({ icon: Icon, label, value }: RowProps) {
  return (
    <div className="flex items-center gap-3">
      {Icon ? (
        <Icon
          className="text-muted-foreground size-4 shrink-0"
          aria-hidden="true"
        />
      ) : (
        <div className="size-4 shrink-0" aria-hidden="true" />
      )}
      <span className="text-muted-foreground w-32 shrink-0">{label}</span>
      <span className="text-foreground truncate font-medium">{value}</span>
    </div>
  )
}
